const BASE = '/api';
let csrfToken = null;
let csrfFetching = null;
let isRefreshing = false;
let queue = [];

function flushQueue(ok) { queue.forEach(cb => cb(ok)); queue = []; }

async function fetchCsrf() {
  if (csrfToken) return csrfToken;
  if (csrfFetching) return csrfFetching;
  csrfFetching = fetch(`${BASE}/csrf-token`, { credentials: 'include' })
    .then(r => r.json()).then(d => { csrfToken = d.csrfToken; csrfFetching = null; return csrfToken; });
  return csrfFetching;
}

export function invalidateCsrf() { csrfToken = null; }

async function parseJson(res) {
  const text = await res.text();
  try { return JSON.parse(text); }
  catch { return {}; }
}

// /auth/me se consulta en todas las páginas para saber si hay sesión: un 401 ahí
// solo significa "visitante sin cuenta" y no debe sacarlo de la portada.
// El panel de comercios tiene su propio ingreso: un 401 ahí no manda al ingreso de usuarios.
const shouldRedirect = (path) => path !== '/auth/me' && !path.startsWith('/comercio')
  && !window.location.pathname.startsWith('/auth') && !window.location.pathname.startsWith('/comercio')
  && !window.location.pathname.startsWith('/descuentos');

async function req(method, path, body, retry = false) {
  const headers = {};
  const isForm = body instanceof FormData;
  if (!isForm) headers['Content-Type'] = 'application/json';
  if (!['GET','HEAD','OPTIONS'].includes(method)) {
    try { headers['X-CSRF-Token'] = await fetchCsrf(); }
    catch { /* si el server está caído, intentar sin CSRF */ }
  }

  let res;
  try {
    res = await fetch(`${BASE}${path}`, {
      method, headers, credentials: 'include', cache: 'no-store',
      body: body == null ? undefined : isForm ? body : JSON.stringify(body),
    });
  } catch (networkErr) {
    throw new Error('Sin conexión. Verificá tu internet.');
  }

  const contentType = res.headers.get('content-type') || '';
  if (!contentType.includes('application/json') && !res.ok) {
    if (res.status >= 500) throw new Error('El servidor no está disponible. Intentá en unos minutos.');
    if (res.status === 502 || res.status === 503 || res.status === 504)
      throw new Error('El servidor no está disponible. Intentá en unos minutos.');
  }

  if (res.status === 401 && !retry) {
    const data = await parseJson(res);
    if (data.code === 'TOKEN_EXPIRED') {
      if (isRefreshing) return new Promise((resolve, reject) => queue.push(ok => ok ? resolve(req(method, path, body, true)) : reject(new Error('Sesión expirada.'))));
      isRefreshing = true;
      try {
        const csrf = await fetchCsrf();
        const rr = await fetch(`${BASE}/auth/refresh`, { method: 'POST', headers: { 'X-CSRF-Token': csrf }, credentials: 'include' });
        if (!rr.ok) throw new Error('refresh failed');
        isRefreshing = false; flushQueue(true);
        return req(method, path, body, true);
      } catch {
        isRefreshing = false; flushQueue(false);
        if (shouldRedirect(path)) window.location.href = '/auth';
        throw new Error('Tu sesión expiró. Ingresá nuevamente.');
      }
    }
    if (shouldRedirect(path)) window.location.href = '/auth';
    throw new Error(data.error || 'Tu sesión expiró. Ingresá nuevamente.');
  }
  if (!res.ok) { const data = await parseJson(res); throw new Error(data.error || `Error ${res.status}`); }
  if (res.status === 204) return null;
  return res.json();
}

export const api = {
  get:    (path)       => req('GET',    path),
  post:   (path, body) => req('POST',   path, body),
  patch:  (path, body) => req('PATCH',  path, body),
  delete: (path)       => req('DELETE', path),

  // Auth
  register:           (data)          => req('POST', '/auth/register', data),
  login:              (email, pass)   => req('POST', '/auth/login', { email, password: pass }),
  logout:             ()              => req('POST', '/auth/logout'),
  me:                 ()              => req('GET',  '/auth/me'),
  verifyEmail:        (userId, token) => req('POST', '/auth/verify-email', { userId, token }),
  forgotPassword:     (email)         => req('POST', '/auth/forgot-password', { email }),
  resetPassword:      (data)          => req('POST', '/auth/reset-password', data),
  resendVerification: (email)         => req('POST', '/auth/resend-verification', { email }),

  // Profile
  updateProfile:  (data)     => req('PATCH', '/profile/me', data),
  uploadPhoto:    (formData) => req('POST',  '/profile/me/photo', formData),
  getProfile:     (id)       => req('GET',   `/profile/${id}`),
  profileOptions: ()         => req('GET',   '/profile/options/all'),

  // Explore
  getProfiles:   (params={}) => {
    const filtered = Object.entries(params).filter(([,v]) => v && v !== 'Todos');
    const qs = new URLSearchParams(filtered).toString();
    return req('GET', `/explore${qs ? '?' + qs : ''}`);
  },
  likeUser:      (id, tipo='like') => req('POST', `/explore/like/${id}`, { tipo }),
  skipUser:      (id)              => req('POST', `/explore/skip/${id}`),
  rewind:        ()                => req('POST', '/explore/rewind'),
  winkUser:      (id)              => req('POST', `/explore/wink/${id}`),
  blockUser:     (id)              => req('POST',   `/explore/block/${id}`),
  unblockUser:   (id)              => req('DELETE', `/explore/block/${id}`),
  getBlockStatus:(id)              => req('GET',    `/explore/block-status/${id}`),
  getWinks:      ()                => req('GET',  '/explore/winks'),
  whoLikedMe:    ()                => req('GET',  '/explore/who-liked-me'),
  whoViewedMe:   ()                => req('GET',  '/explore/who-viewed-me'),
  viewProfile:   (id)              => req('POST', `/explore/view/${id}`),
  getMatches:    ()                => req('GET',  '/explore/matches'),
  getStandouts:  ()                => req('GET',  '/explore/standouts'),
  impulsoSemanal:()                => req('POST', '/explore/impulso-semanal'),

  // Verificación con selfie
  verificacionEstado: ()         => req('GET',  '/verificacion'),
  enviarSelfie:       (formData) => req('POST', '/verificacion', formData),

  // Chat
  getMessages:       (matchId, before) => req('GET',   `/chat/${matchId}/messages${before ? '?before=' + before : ''}`),
  sendMessage:       (matchId, text)   => req('POST',  `/chat/${matchId}/messages`, { text }),
  markMessagesRead:  (matchId)         => req('PATCH', `/chat/${matchId}/messages/read`),

  // Reports
  createReport:    (data) => req('POST', '/reports', data),
  appReport:       (data) => req('POST', '/reports/app', data),

  // Payments
  paymentStatus:      ()      => req('GET',  '/payments/status'),
  createPreference:   (plan)  => req('POST', '/payments/create-preference', { plan }),
  cancelSubscription: ()      => req('POST', '/payments/cancel'),

  // Account
  deleteAccount: (data) => req('DELETE', '/auth/account', data),

  // AI chatbot
  aiStatus: ()              => req('GET',  '/ai/status'),
  aiChat:   (message, history) => req('POST', '/ai/chat', { message, history }),

  // Photos
  deletePhoto: (photoPath) => req('DELETE', '/profile/me/photo', { path: photoPath }),

  // Admin
  adminStats:             ()                    => req('GET',    '/admin/stats'),
  adminUsers:             (search, page)        => req('GET',    `/admin/users?search=${search||''}&page=${page||1}`),
  adminSetStatus:         (id, status, note)    => req('PATCH',  `/admin/users/${id}/status`, { status, adminNote: note }),
  adminDeleteUser:        (id)                  => req('DELETE', `/admin/users/${id}`),
  adminReports:           ()                    => req('GET',    '/admin/reports'),
  adminResolveReport:     (id, status, note)    => req('PATCH',  `/admin/reports/${id}`, { status, adminNote: note }),
  adminChatBetween:       (userA, userB)        => req('GET',    `/admin/chat-between?userA=${userA}&userB=${userB}`),
  adminSetRole:           (id, role)            => req('PATCH',  `/admin/users/${id}/role`, { role }),
  adminAppReports:        (status)              => req('GET',    `/admin/app-reports${status ? '?status=' + status : ''}`),
  adminResolveAppReport:  (id, status, note)    => req('PATCH',  `/admin/app-reports/${id}`, { status, adminNote: note }),
  adminInfluencer:        (id, data)            => req('PATCH',  `/admin/users/${id}/influencer`, data),
  adminInfluencers:       ()                    => req('GET',    '/admin/influencers'),
  regalosCatalogo:        ()                    => req('GET',    '/regalos/catalogo'),
  crearRegalo:            (data)                => req('POST',   '/regalos', data),
  misRegalos:             ()                    => req('GET',    '/regalos'),
  aceptarRegalo:          (id, data)            => req('POST',   `/regalos/${id}/aceptar`, data),
  rechazarRegalo:         (id)                  => req('POST',   `/regalos/${id}/rechazar`),
  adminComercios:         ()                    => req('GET',    '/admin/comercios'),
  adminCrearComercio:     (data)                => req('POST',   '/admin/comercios', data),
  adminEditarComercio:    (id, data)            => req('PATCH',  `/admin/comercios/${id}`, data),
  adminCrearProducto:     (comercioId, data)    => req('POST',   `/admin/comercios/${comercioId}/productos`, data),
  adminEditarProducto:    (id, data)            => req('PATCH',  `/admin/productos/${id}`, data),
  adminFotoProducto:      (id, formData)        => req('POST',   `/admin/productos/${id}/foto`, formData),
  adminRegalos:           ()                    => req('GET',    '/admin/regalos'),
  adminLogoComercio:      (id, formData)        => req('POST',   `/admin/comercios/${id}/logo`, formData),
  adminAccesoComercio:    (id)                  => req('POST',   `/admin/comercios/${id}/acceso`),
  adminEstadoComercio:    (id, estado)          => req('PATCH',  `/admin/comercios/${id}/estado`, { estado }),
  // Panel de comercios (/comercio)
  comercioYo:             ()                    => req('GET',    '/comercio/yo'),
  comercioAsistente:      (message, history, marca) => req('POST', '/comercio/asistente', { message, history, marca }),
  comercioGooglePendiente:()                    => req('GET',    '/comercio/google/pendiente'),
  comercioNacimiento:     (fecha_nacimiento)    => req('PUT',    '/comercio/nacimiento', { fecha_nacimiento }),
  comercioIngresar:       (email, password)     => req('POST',   '/comercio/ingresar', { email, password }),
  comercioRegistro:       (data)                => req('POST',   '/comercio/registro', data),
  comercioSalir:          ()                    => req('POST',   '/comercio/salir'),
  comercioActivar:        (token, password)     => req('POST',   '/comercio/activar', { token, password }),
  comercioOlvide:         (email)               => req('POST',   '/comercio/olvide', { email }),
  comercioTerminos:       ()                    => req('POST',   '/comercio/terminos', { acepto: 'true' }),
  comercioEditar:         (data)                => req('PATCH',  '/comercio/yo', data),
  comercioHorarios:       (horarios)            => req('PUT',    '/comercio/horarios', { horarios }),
  comercioZona:           (data)                => req('PUT',    '/comercio/zona', data),
  comercioLogo:           (formData)            => req('POST',   '/comercio/logo', formData),
  comercioProductos:      ()                    => req('GET',    '/comercio/productos'),
  comercioCrearProducto:  (data)                => req('POST',   '/comercio/productos', data),
  comercioEditarProducto: (id, data)            => req('PATCH',  `/comercio/productos/${id}`, data),
  comercioFotoProducto:   (id, formData)        => req('POST',   `/comercio/productos/${id}/foto`, formData),
  comercioPedidos:        ()                    => req('GET',    '/comercio/pedidos'),
  comercioEnviado:        (id, medio)           => req('POST',   `/comercio/pedidos/${id}/enviado`, { medio }),
  comercioEntregado:      (id)                  => req('POST',   `/comercio/pedidos/${id}/entregado`),
  comercioDescuentos:     ()                    => req('GET',    '/comercio/descuentos'),
  comercioCrearDescuento: (data)                => req('POST',   '/comercio/descuentos', data),
  comercioEditarDescuento:(id, data)            => req('PATCH',  `/comercio/descuentos/${id}`, data),
  comercioValidarCodigo:  (codigo)              => req('POST',   '/comercio/descuentos/validar', { codigo }),
  comercioDestacadoPrueba:()                    => req('POST',   '/comercio/descuentos/destacado/prueba'),
  comercioDestacadoSuscribir:(panel)            => req('POST',   '/comercio/descuentos/destacado/suscribir', { panel }),
  comercioDestacadoBaja:  ()                    => req('POST',   '/comercio/descuentos/destacado/baja'),
  adminAlertas:           (estado)              => req('GET',    `/admin/alertas?estado=${estado || 'pendiente'}`),
  adminRevisarAlerta:     (id)                  => req('PATCH',  `/admin/alertas/${id}`),
  adminRevisarFoto:       (id, accion)          => req('PATCH',  `/admin/fotos-revision/${id}`, { accion }),
  // Nota de 24 h, test de afinidad, descuentos, compras sueltas y cita segura
  guardarNota:            (nota)                => req('PUT',    '/profile/me/nota', { nota }),
  borrarNota:             ()                    => req('DELETE', '/profile/me/nota'),
  compatTest:             ()                    => req('GET',    '/compat'),
  guardarCompat:          (respuestas)          => req('PUT',    '/compat', { respuestas }),
  descuentos:             ()                    => req('GET',    '/descuentos'),
  lugarRecomendado:       ()                    => req('GET',    '/descuentos/recomendado'),
  codigoDescuento:        (id)                  => req('POST',   `/descuentos/${id}/codigo`),
  adminDescuentos:        ()                    => req('GET',    '/descuentos/admin/todos'),
  adminCrearDescuento:    (data)                => req('POST',   '/descuentos/admin', data),
  adminEditarDescuento:   (id, data)            => req('PUT',    `/descuentos/admin/${id}`, data),
  adminBorrarDescuento:   (id)                  => req('DELETE', `/descuentos/admin/${id}`),
  adminPausarDescuento:   (id, pausado)         => req('PATCH',  `/descuentos/admin/${id}/pausa`, { pausado }),
  compras:                ()                    => req('GET',    '/payments/compras'),
  comprar:                (producto)            => req('POST',   '/payments/compra', { producto }),
  citaSeguraActiva:       (matchId)             => req('GET',    `/cita-segura/activa?match=${encodeURIComponent(matchId || '')}`),
  crearCitaSegura:        (data)                => req('POST',   '/cita-segura', data),
  pedirAyudaCita:         (id, data)            => req('POST',   `/cita-segura/${id}/ayuda`, data),
  terminarCitaSegura:     (id)                  => req('POST',   `/cita-segura/${id}/terminar`),
  citaSeguraPublica:      (token)               => req('GET',    `/cita-segura/publica/${encodeURIComponent(token)}`),
};
